import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'rsuite';
import { getOptimizationPriceManagerDetails } from '../../../pages/IPA/OtimizacaoPrecos/services';
import { handleModalState, selectorModalOptimizationSummary } from '../../../reducers/gerenciadorPrecos/optimizationPriceEdited';
import { weeklyOptions } from '../../../reducers/OtimizacaoPrecos/optimizationPrices';
import { OptimizationSummaryResponse } from '../types/optimizationSummaryResponse';

const periodStatus = {
  weekly: 'Semanal',
  monthly: 'Mensal',
};

const objectiveStatus = {
  profit: 'Maximizar Lucro',
  revenue: 'Maximizar Receita',
};

export function useModalOptimizationSummary() {
  const { modalIsOpen, rowData } = useSelector(selectorModalOptimizationSummary);

  const { data } = useQuery<OptimizationSummaryResponse>({
    queryKey: ['gerenciador-precos-optimization-summary', rowData?.optimizationId],
    retry: true,
    keepPreviousData: true,
    enabled: !!rowData?.optimizationId,
    queryFn: () => getOptimizationPriceManagerDetails(rowData?.optimizationId as string),
  });

  const dispatch = useDispatch();

  const handleCloseModal = () => {
    dispatch(handleModalState({ modalIsOpen: false }));
  };

  const redirectToOptimization = () => {
    handleCloseModal();
    if (!rowData?.optimizationId) {
      Alert.warning('Não foi possível encontrar a otimização');
      return;
    }
    window.open(`/ipa/ia/otimizacao-de-precos/resultado/${rowData?.optimizationId}`, '_blank');
  };

  const mapApiToForm = (data: OptimizationSummaryResponse) => {
    if (!data) {
      return;
    }
    return {
      filters: {
        categories: data.categories?.join(', '),
        storesName: data.stores?.map(store => store.store_name) || [],
        controlStoreName: data?.control_store?.store_name || '--',
      },
      coordinates: {
        objective: objectiveStatus[data.objective as keyof typeof objectiveStatus] as 'profit' | 'revenue',
        margin: {
          min: data.restrictions.min_margin !== null ? `${data.restrictions.min_margin}%` : '--',
          max: data.restrictions.max_margin !== null ? `${data.restrictions.max_margin}%` : '--',
        },
        competitiveness: {
          min: data.restrictions.min_competitiveness !== null ? `${data.restrictions.min_competitiveness}%` : '--',
          max: data.restrictions.max_competitiveness !== null ? `${data.restrictions.max_competitiveness}%` : '--',
        },
        minVariationToChangePrice: Number(data.restrictions.variation ?? 0),
      },
      scheduleOptimizationPrices: {
        period: {
          value: periodStatus[data.recalculate.period as keyof typeof periodStatus] as keyof typeof periodStatus,
          options:
            weeklyOptions
              .filter(day => data.recalculate.days_of_week.includes(day.value))
              ?.map(day => day.label)
              ?.join(', ') || 'Segunda-feira',
        },
        validity: {
          start: {
            startDate: data.validity.start_date ? format(new Date(data.validity.start_date + 'T00:00:00'), 'dd/MM/yyyy') : '--',
          },
          end: {
            endDate: data.validity.end_date ? format(new Date(data.validity.end_date + 'T00:00:00'), 'dd/MM/yyyy') : '--',
          },
        },
        execution: {
          lastExecutionDate: data.last_execution ? format(new Date(data.last_execution + 'T00:00:00'), 'dd/MM/yyyy') : '--',
          nextExecutionDate: data.next_execution ? format(new Date(data.next_execution + 'T00:00:00'), 'dd/MM/yyyy') : '--',
        },
      },
      responsible: data.responsible,
    };
  };

  return {
    modalIsOpen,
    handleCloseModal,
    redirectToOptimization,
    optimizationSummaryData: mapApiToForm(data as OptimizationSummaryResponse),
  };
}
